import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../../App';
import customerPortalService from '../../services/customerPortalService';
import { ApiKeyList } from '../apiKeyManagement/ApiKeyList';

// Customer self-service portal: a logged-in customer-org user manages their own
// API keys for the products their provider has enabled. The pricing plan/tier is
// resolved server-side, so there is intentionally no plan picker here.
export default function CustomerPortal() {
  const { selectedTeam } = useContext(AppContext);
  const teamId = selectedTeam?.teamId;

  const [products, setProducts] = useState([]);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [selectedProductId, setSelectedProductId] = useState('');
  const [nickname, setNickname] = useState('');
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError('');
    try {
      const [prods, ks] = await Promise.all([
        customerPortalService.getProducts(teamId),
        customerPortalService.getKeys(teamId)
      ]);
      setProducts(prods);
      setKeys(ks);
    } catch (err) {
      console.error('Failed to load portal data:', err);
      setError(err.message || 'Failed to load your account');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    load();
  }, [load]);

  const canGenerate = useMemo(
    () => !!selectedProductId && !!nickname.trim() && !generating,
    [selectedProductId, nickname, generating]
  );

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setGenerating(true);
    setError('');
    setSuccess('');
    try {
      await customerPortalService.createKey(teamId, selectedProductId, nickname.trim());
      setNickname('');
      setSelectedProductId('');
      setSuccess('API key created. Reveal it in the list below to copy it.');
      await load();
    } catch (err) {
      console.error('Failed to create key:', err);
      setError(err.message || 'Failed to create API key');
    } finally {
      setGenerating(false);
    }
  };

  const handleUpdateNickname = async (keyId, newNickname) => {
    setError('');
    try {
      await customerPortalService.updateKey(keyId, { nickname: newNickname });
      setKeys((prev) => prev.map((k) => (k.id === keyId ? { ...k, nickname: newNickname } : k)));
    } catch (err) {
      setError(err.message || 'Failed to rename key');
    }
  };

  const handleToggleKey = async (keyId, isCurrentlyEnabled) => {
    setError('');
    try {
      await customerPortalService.updateKey(keyId, { isEnabled: !isCurrentlyEnabled });
      setKeys((prev) =>
        prev.map((k) => (k.id === keyId ? { ...k, is_enabled: !isCurrentlyEnabled } : k))
      );
    } catch (err) {
      setError(err.message || 'Failed to update key');
    }
  };

  const handleDeleteKeyConfirm = async (keyId) => {
    setError('');
    try {
      await customerPortalService.deleteKey(keyId);
      setKeys((prev) => prev.filter((k) => k.id !== keyId));
      setSuccess('API key deleted.');
    } catch (err) {
      setError(err.message || 'Failed to delete key');
    }
  };

  if (!teamId) {
    return <div className="text-slate-400">No account selected.</div>;
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-100">API Keys</h1>
        <p className="text-sm text-slate-400 mt-1">
          Create and manage your API keys for <strong>{selectedTeam?.teamName}</strong>.
        </p>
      </div>

      {error && (
        <div className="bg-red-950/30 border border-red-800/50 text-red-300 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-emerald-950/30 border border-emerald-800/50 text-emerald-300 px-4 py-3 rounded-lg mb-4">
          {success}
        </div>
      )}

      {/* Generate a new key */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 mb-8">
        <h2 className="text-base font-semibold text-slate-100 mb-4">Create a new API key</h2>
        {products.length === 0 ? (
          <p className="text-sm text-slate-400">
            No products are enabled for self-service on your account yet. Contact your provider to
            get access.
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-3 sm:items-end">
            <div>
              <label className="block text-sm mb-1 text-slate-400">Product</label>
              <select
                className="w-full p-2 bg-slate-900 rounded border border-slate-700 text-slate-200 [&>option]:bg-slate-800 focus:outline-none focus:border-slate-600"
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
              >
                <option value="">Select a product…</option>
                {products.map((p) => (
                  <option key={p.productId} value={p.productId}>
                    {p.productName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1 text-slate-400">Key name</label>
              <input
                type="text"
                placeholder="e.g. Production server"
                className="w-full p-2 bg-slate-900 rounded border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-slate-600"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleGenerate();
                }}
              />
            </div>
            <button
              onClick={handleGenerate}
              disabled={!canGenerate}
              className="py-2 px-4 bg-indigo-600 rounded hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-white font-medium text-sm h-[40px]"
            >
              {generating ? 'Creating…' : 'Create key'}
            </button>
          </div>
        )}
      </div>

      {/* Existing keys */}
      <h2 className="text-base font-semibold text-slate-100 mb-3">Your API keys</h2>
      {loading ? (
        <div className="flex items-center text-slate-400">
          <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mr-3" />
          Loading…
        </div>
      ) : (
        <ApiKeyList
          apiKeys={keys}
          handleUpdateNickname={handleUpdateNickname}
          handleToggleKey={handleToggleKey}
          handleDeleteKeyConfirm={handleDeleteKeyConfirm}
          setSuccess={setSuccess}
          setError={setError}
        />
      )}
    </div>
  );
}
